import { useState, useEffect, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import type { KnowledgeFile, KnowledgeUpdateStatus } from '@craft-agent/relay-backend/types'

interface KnowledgeItem {
  slug: string
  title: string
  updatedAt: string
  syncStatus: 'synced' | 'modified' | 'new' | 'conflicted' | 'remote-updated'
  fileCount?: number
}

export function useKnowledgeSync(workspaceId: string, sourceSlug: string, enabled: boolean) {
  const [items, setItems] = useState<KnowledgeItem[]>([])
  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null)
  const [updateStatus, setUpdateStatus] = useState<KnowledgeUpdateStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [files, setFiles] = useState<Record<string, KnowledgeFile[]>>({})

  const fetchItems = useCallback(async () => {
    if (!enabled) return
    try {
      const result = await window.electronAPI.knowledgeListItems(workspaceId, sourceSlug)
      setItems(result)
      setError(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch items'
      setError(message)
    }
  }, [workspaceId, sourceSlug, enabled])

  const checkUpdates = useCallback(async () => {
    if (!enabled) return
    try {
      const status = await window.electronAPI.knowledgeCheckUpdates(workspaceId, sourceSlug)
      setUpdateStatus(status)
    } catch {
      // Non-critical — don't set error state for update checks
    }
  }, [workspaceId, sourceSlug, enabled])

  // Auto-refresh local items on window focus (local disk read only, no network)
  useEffect(() => {
    if (!enabled) return
    const handleFocus = () => { fetchItems() }
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [enabled, fetchItems])

  const pull = useCallback(async () => {
    setIsSyncing(true)
    setError(null)
    try {
      const result = await window.electronAPI.knowledgePull(workspaceId, sourceSlug)
      if (result.success) {
        setLastSyncedAt(new Date().toISOString())
        const parts = [
          result.itemsCreated && `${result.itemsCreated} created`,
          result.itemsUpdated && `${result.itemsUpdated} updated`,
          result.itemsDeleted && `${result.itemsDeleted} deleted`,
          result.itemsConflicted && `${result.itemsConflicted} conflicts`,
        ].filter(Boolean)
        toast.success('Knowledge synced', {
          description: parts.join(', ') || 'Everything up to date',
        })
        await fetchItems()
        await checkUpdates()
      } else {
        const message = result.errors.join(', ') || 'Sync failed'
        setError(message)
        toast.error('Sync failed', { description: message })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sync failed'
      setError(message)
      toast.error('Sync failed', { description: message })
    } finally {
      setIsSyncing(false)
    }
  }, [workspaceId, sourceSlug, fetchItems, checkUpdates])

  // Auto-pull on mount: fetch local items, check for remote updates, and pull if any exist
  const autoPullAttempted = useRef(false)
  useEffect(() => {
    if (!enabled) return
    fetchItems()
    const autoSync = async () => {
      try {
        const status = await window.electronAPI.knowledgeCheckUpdates(workspaceId, sourceSlug)
        setUpdateStatus(status)
        if (status.hasRemoteUpdates && !autoPullAttempted.current) {
          autoPullAttempted.current = true
          await pull()
        }
      } catch {
        // Non-critical
      }
    }
    autoSync()
  }, [enabled, fetchItems, workspaceId, sourceSlug, pull])

  const pushAll = useCallback(async () => {
    setIsSyncing(true)
    setError(null)
    try {
      const result = await window.electronAPI.knowledgePushAll(workspaceId, sourceSlug)
      if (result.success) {
        setLastSyncedAt(new Date().toISOString())
        toast.success('Changes pushed', {
          description: `${result.pushedCount} item${result.pushedCount !== 1 ? 's' : ''} pushed`,
        })
        await fetchItems()
        await checkUpdates()
      } else {
        const message = result.errors.join(', ') || 'Push failed'
        setError(message)
        toast.error('Push failed', { description: message })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Push failed'
      setError(message)
      toast.error('Push failed', { description: message })
    } finally {
      setIsSyncing(false)
    }
  }, [workspaceId, sourceSlug, fetchItems, checkUpdates])

  const deleteItem = useCallback(async (itemSlug: string) => {
    setIsSyncing(true)
    setError(null)
    try {
      const result = await window.electronAPI.knowledgeDeleteItem(workspaceId, sourceSlug, itemSlug)
      if (result.success) {
        toast.success('Item deleted')
        await fetchItems()
        await checkUpdates()
      } else {
        const message = result.error || 'Delete failed'
        setError(message)
        toast.error('Delete failed', { description: message })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Delete failed'
      setError(message)
      toast.error('Delete failed', { description: message })
    } finally {
      setIsSyncing(false)
    }
  }, [workspaceId, sourceSlug, fetchItems, checkUpdates])

  const createItem = useCallback(async (title: string) => {
    setError(null)
    try {
      const result = await window.electronAPI.knowledgeCreateItem(workspaceId, sourceSlug, title)
      if (result.success) {
        toast.success('Item created', {
          description: `"${title}" created locally. Push to share with the team.`,
        })
        await fetchItems()
        await checkUpdates()
        return result.slug
      } else {
        const message = result.error || 'Create failed'
        setError(message)
        toast.error('Create failed', { description: message })
        return null
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Create failed'
      setError(message)
      toast.error('Create failed', { description: message })
      return null
    }
  }, [workspaceId, sourceSlug, fetchItems, checkUpdates])

  const listFiles = useCallback(async (itemSlug: string) => {
    try {
      const result = await window.electronAPI.knowledgeListFiles(workspaceId, sourceSlug, itemSlug)
      setFiles(prev => ({ ...prev, [itemSlug]: result }))
    } catch {
      // Non-critical
    }
  }, [workspaceId, sourceSlug])

  const uploadFile = useCallback(async (itemSlug: string) => {
    try {
      const result = await window.electronAPI.knowledgeUploadFile(workspaceId, sourceSlug, itemSlug)
      if (result.success && result.file) {
        toast.success('File attached', {
          description: result.file.filename,
        })
        await listFiles(itemSlug)
      } else if (result.error && result.error !== 'No file selected') {
        toast.error('Upload failed', { description: result.error })
      }
    } catch (err) {
      toast.error('Upload failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }, [workspaceId, sourceSlug, listFiles])

  const deleteFile = useCallback(async (fileId: string, itemSlug: string, filename: string) => {
    try {
      const result = await window.electronAPI.knowledgeDeleteFile(workspaceId, sourceSlug, itemSlug, fileId, filename)
      if (result.success) {
        toast.success('File deleted')
        await listFiles(itemSlug)
      } else {
        toast.error('Delete failed', { description: result.error })
      }
    } catch (err) {
      toast.error('Delete failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }, [workspaceId, sourceSlug, listFiles])

  const replaceFile = useCallback(async (fileId: string, itemSlug: string, filename: string) => {
    try {
      const result = await window.electronAPI.knowledgeReplaceFile(workspaceId, sourceSlug, itemSlug, fileId, filename)
      if (result.success && result.file) {
        toast.success('File replaced', {
          description: result.file.filename,
        })
        await listFiles(itemSlug)
      } else if (result.error && result.error !== 'No file selected') {
        toast.error('Replace failed', { description: result.error })
      }
    } catch (err) {
      toast.error('Replace failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }, [workspaceId, sourceSlug, listFiles])

  const refresh = useCallback(async () => {
    await fetchItems()
    await checkUpdates()
  }, [fetchItems, checkUpdates])

  return {
    items,
    isSyncing,
    lastSyncedAt,
    updateStatus,
    pull,
    pushAll,
    refresh,
    error,
    deleteItem,
    createItem,
    files,
    listFiles,
    uploadFile,
    deleteFile,
    replaceFile,
  }
}
